import {addQueryParamsToRelativeUrl} from "@khan/assignments-utils";
import type {UserAttributes} from "@khan/shared-core";

import {
    getPolarisRedirectUrl,
    shouldRedirectToKaClassroom,
} from "#shared-package/polaris-redirect-util.ts";

import type {AssignmentPath, Classroom, TopicPath} from "../../types/types.ts";

import {constructURL} from "./content-util.ts";

export type Assignment = {
    path: AssignmentPath;
    content:
        | {
              contentKind: string;
          }
        | null
        | undefined;
    id: string;
    key: string;
    isSameQuestion: boolean;
};

type Content = {
    topicPaths: Array<TopicPath>;
    urlWithinCurationNode: string;
};

/**
 * Returns the URL to use for an assignment based on the DCUL context where it
 * was assigned.
 */
export const getAssignmentURL = (
    assignment: Assignment,
    content: Content,
    classCode?: string | null,
): string => {
    // TODO (LP-11727) Replace this with the assignment's urlWithinCurationNode
    // if/when we get ancestorID hooked up for cross-service requests
    const path = findPathUnderNodes(content.topicPaths, assignment.path);

    // Default to the canonical URL if no path was found
    let url = path
        ? (constructURL(path) ?? content.urlWithinCurationNode)
        : content.urlWithinCurationNode;

    if (classCode) {
        // Pass through the classCode URL param, if it exists, so that we don't
        // switch classrooms as you navigate to different content
        url = addQueryParamsToRelativeUrl(url, [
            {key: "classCode", value: classCode},
        ]);
    }
    return addAssignmentKeyIfNeeded(url, assignment);
};

/**
 * Returns a URL for the user's assignments in a specific classroom in LIHP.
 * If no classroom is provided, it returns the default assignments homepage URL.
 */
export const getHomepageURL = (
    classroom?: Classroom | null,
    userAttributes?: UserAttributes | null,
): string => {
    if (classroom == null) {
        return "/profile/me/assignments";
    }

    // Check if this classroom should redirect to Polaris
    if (shouldRedirectToKaClassroom(classroom.isKhanClassroom)) {
        return getPolarisRedirectUrl(
            classroom.signupCode,
            userAttributes ?? null,
        );
    }

    return `/profile/me/assignments/teacher/${classroom.coachKaid}/class/${classroom.id}`;
};

/**
 * Helper function used by LIHP to build a URL without using the 321 types
 */
export const findPathUnderNodes = (
    topicPaths: Array<TopicPath>,
    assignmentPath: AssignmentPath,
): TopicPath | null | undefined => {
    const {domainId, courseId, unitId, lessonId} = assignmentPath;

    if (!domainId && !courseId && !unitId && !lessonId) {
        // No DCUL information was provided; return null
        return null;
    }

    return (
        topicPaths.find((path) => {
            if (
                lessonId &&
                (path.pathKind === "course-level-learnable-content" ||
                    path.lessonId !== lessonId)
            ) {
                return false;
            }
            if (
                unitId &&
                (path.pathKind === "course-level-learnable-content" ||
                    path.unitId !== unitId)
            ) {
                return false;
            }
            if (courseId && path.courseId !== courseId) {
                return false;
            }
            if (domainId && path.domainId !== domainId) {
                return false;
            }

            // If all present DCUL fields match, use this path.
            return true;
        }) ?? null
    );
};

/**
 * Helper function to add the assignment key query param to a URL if it's needed
 * - if the assignment is an Exercise with same questions selected
 * - if the assignment is a Khanmigo activity
 */
export const addAssignmentKeyIfNeeded = (
    url: string,
    assignment: Assignment | null,
): string => {
    if (assignment?.content?.contentKind === "AIGuideActivity") {
        if (!assignment?.id) {
            return url;
        }
        return addQueryParamsToRelativeUrl(url, [
            {key: "assignment", value: assignment.id},
        ]);
    }

    if (!assignment?.isSameQuestion || !assignment?.key) {
        return url;
    }

    return addQueryParamsToRelativeUrl(url, [
        {key: "assignment", value: assignment.key},
    ]);
};
